import React, { useState, useEffect, useCallback } from 'react';
import { Link, useParams, useNavigate } from 'react-router-dom';
import superAdminApiClient from '../../../api/superAdminApiClient';
import SEO from '../../../components/common/SEO';
import { toast } from 'react-toastify';
import ContentLoader from '../../../components/common/ContentLoader';
import { formatDateTime } from '../../../utils/dateUtils';

const MODULE_CONFIG = {
    'holidays': { backendKey: 'holiday-package', label: 'Holiday Packages' },
    'hotels': { backendKey: 'hotel', label: 'Hotels' },
    'cabs': { backendKey: 'cab', label: 'Cabs' },
    'doctors': { backendKey: 'doctor', label: 'Doctors' },
    'hospitals': { backendKey: 'hospital', label: 'Hospitals' },
    'yatras': { backendKey: 'yatra', label: 'Yatras' },
};

const ListingApprovals = () => {
    const { module } = useParams();
    const navigate = useNavigate();

    // Fallback if invalid module
    useEffect(() => {
        if (!MODULE_CONFIG[module]) {
            navigate('/superadmin/dashboard');
        }
    }, [module, navigate]);

    const currentConfig = MODULE_CONFIG[module] || MODULE_CONFIG['holidays'];
    const backendModule = currentConfig.backendKey;

    const [listings, setListings] = useState([]);
    const [loading, setLoading] = useState(false);

    // Filter States
    const [searchTitle, setSearchTitle] = useState('');
    const [vendorName, setVendorName] = useState('');
    const [mobile, setMobile] = useState('');
    const [status, setStatus] = useState('');

    // Pagination States
    const [page, setPage] = useState(1);
    const [limit, setLimit] = useState(20);
    const [pagination, setPagination] = useState({ total: 0, page: 1, limit: 20, totalPages: 1 });

    const fetchListings = useCallback(async () => {
        setLoading(true);
        try {
            const params = {
                page,
                limit,
                search: searchTitle,
                status: status === 'all' ? '' : status,
                vendor_name: vendorName,
                mobile: mobile
            };
            const response = await superAdminApiClient.get(`/superadmin/listings/${backendModule}`, { params });
            if (response.data && response.data.success) {
                setListings(response.data.data || []);
                if (response.data.pagination) {
                    setPagination(response.data.pagination);
                }
            } else {
                setListings(response.data || []);
            }
        } catch (error) {
            console.error('Error fetching listings', error);
            toast.error('Failed to load listings');
        } finally {
            setLoading(false);
        }
    }, [backendModule, page, limit, searchTitle, status, vendorName, mobile]);

    useEffect(() => {
        if (MODULE_CONFIG[module]) {
            fetchListings();
        }
    }, [module, fetchListings]);

    // Reset page to 1 on filter change
    const handleFilterChange = (setter, value) => {
        setter(value);
        setPage(1);
    };

    const handleResetFilters = () => {
        setSearchTitle('');
        setVendorName('');
        setMobile('');
        setStatus('');
        setPage(1);
    };

    const renderStatusBadge = (itemStatus) => {
        let badgeClass = 'bg-secondary';
        let label = itemStatus || 'Published';

        if (itemStatus === 'Published') {
            badgeClass = 'bg-success';
        } else if (itemStatus === 'Under Review') {
            badgeClass = 'bg-warning text-dark';
            label = 'Under Review (Pending)';
        } else if (itemStatus === 'Rejected') {
            badgeClass = 'bg-danger';
        } else if (itemStatus === 'Deleted') {
            badgeClass = 'bg-dark';
        }

        return <span className={`badge ${badgeClass}`}>{label}</span>;
    };

    return (
        <div className="row">
            <SEO title={`${currentConfig.label} Approvals | Super Admin`} />
            
            <div className="col-lg-12">
                <div className="card">
                    <div className="card-header d-flex justify-content-between align-items-center flex-wrap gap-2">
                        <h4 className="card-title mb-0">{currentConfig.label} Approvals</h4>
                        <span className="badge bg-soft-info text-info fs-13">
                            Total Listings: {pagination.total || listings.length}
                        </span>
                    </div>

                    {/* FILTER TOOLBAR */}
                    <div className="card-body border-bottom bg-light">
                        <div className="row g-3">
                            <div className="col-md-3 col-sm-6">
                                <label className="form-label fw-semibold fs-12 mb-1">Search Listing Title</label>
                                <div className="input-group input-group-sm">
                                    <span className="input-group-text"><i className="ri-search-line"></i></span>
                                    <input
                                        type="text"
                                        className="form-control"
                                        placeholder="Title / Name..."
                                        value={searchTitle}
                                        onChange={(e) => handleFilterChange(setSearchTitle, e.target.value)}
                                    />
                                </div>
                            </div>

                            <div className="col-md-3 col-sm-6">
                                <label className="form-label fw-semibold fs-12 mb-1">Vendor Name / Company</label>
                                <div className="input-group input-group-sm">
                                    <span className="input-group-text"><i className="ri-user-line"></i></span>
                                    <input
                                        type="text"
                                        className="form-control"
                                        placeholder="Vendor name or company..."
                                        value={vendorName}
                                        onChange={(e) => handleFilterChange(setVendorName, e.target.value)}
                                    />
                                </div>
                            </div>

                            <div className="col-md-2 col-sm-6">
                                <label className="form-label fw-semibold fs-12 mb-1">Vendor Mobile</label>
                                <div className="input-group input-group-sm">
                                    <span className="input-group-text"><i className="ri-phone-line"></i></span>
                                    <input
                                        type="text"
                                        className="form-control"
                                        placeholder="Mobile no..."
                                        value={mobile}
                                        onChange={(e) => handleFilterChange(setMobile, e.target.value)}
                                    />
                                </div>
                            </div>

                            <div className="col-md-2 col-sm-6">
                                <label className="form-label fw-semibold fs-12 mb-1">Status</label>
                                <select
                                    className="form-select form-select-sm"
                                    value={status}
                                    onChange={(e) => handleFilterChange(setStatus, e.target.value)}
                                >
                                    <option value="">All Statuses</option>
                                    <option value="Published">Published</option>
                                    <option value="Under Review">Pending / Under Review</option>
                                    <option value="Rejected">Rejected</option>
                                    <option value="Deleted">Deleted</option>
                                </select>
                            </div>

                            <div className="col-md-2 col-sm-12 d-flex align-items-end gap-2">
                                <button
                                    type="button"
                                    className="btn btn-sm btn-soft-secondary w-100"
                                    onClick={handleResetFilters}
                                >
                                    <i className="ri-refresh-line me-1"></i> Reset
                                </button>
                            </div>
                        </div>
                    </div>
                    
                    <div className="card-body">
                        {loading ? (
                            <ContentLoader />
                        ) : (
                            <>
                                <div className="table-responsive">
                                    <table className="table table-bordered table-striped align-middle mb-0">
                                        <thead className="table-light">
                                            <tr>
                                                <th style={{ width: '60px' }}>ID</th>
                                                <th>Listing Title</th>
                                                <th>Vendor Name</th>
                                                <th>Vendor Contact</th>
                                                <th>Status</th>
                                                <th>Created At</th>
                                                <th style={{ width: '120px' }}>Actions</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {listings.length > 0 ? (
                                                listings.map(item => (
                                                    <tr key={item.id}>
                                                        <td className="fw-semibold">#{item.id}</td>
                                                        <td>
                                                            <span className="fw-medium">{item.title || 'N/A'}</span>
                                                            {item.status === 'Rejected' && item.rejection_reason && (
                                                                <div className="text-danger mt-1" style={{ fontSize: '11px', whiteSpace: 'normal', maxWidth: '250px' }}>
                                                                    <i className="ri-error-warning-line align-middle me-1"></i>
                                                                    {item.rejection_reason}
                                                                </div>
                                                            )}
                                                        </td>
                                                        <td>
                                                            {item.vendor?.name && <div><i className="ri-user-line me-1 text-muted"></i>{item.vendor.name}</div>}
                                                            {item.vendor?.company_name && <div className="text-muted fs-12"><i className="ri-building-line me-1"></i>{item.vendor.company_name}</div>}
                                                        </td>
                                                        <td>
                                                            {item.vendor?.email && <div className="fs-12">{item.vendor.email}</div>}
                                                            {item.vendor?.phone && <div className="fs-12 text-muted"><i className="ri-phone-line me-1"></i>{item.vendor.phone}</div>}
                                                        </td>
                                                        <td>{renderStatusBadge(item.status)}</td>
                                                        <td className="fs-12 text-muted">{item.created_at ? formatDateTime(item.created_at) : 'N/A'}</td>
                                                        <td>
                                                            <Link 
                                                                to={`/superadmin/listings-approval/${module}/${item.id}`}
                                                                className="btn btn-sm btn-primary"
                                                            >
                                                                <i className="ri-eye-line me-1"></i> View Details
                                                            </Link>
                                                        </td>
                                                    </tr>
                                                ))
                                            ) : (
                                                <tr>
                                                    <td colSpan="7" className="text-center py-4 text-muted">
                                                        <i className="ri-search-line fs-24 d-block mb-1"></i>
                                                        No listings found matching the selected filters.
                                                    </td>
                                                </tr>
                                            )}
                                        </tbody>
                                    </table>
                                </div>

                                {/* PAGINATION CONTROLS */}
                                {pagination.total > 0 && (
                                    <div className="d-flex justify-content-between align-items-center flex-wrap gap-2 mt-3 pt-3 border-top">
                                        <div className="d-flex align-items-center gap-2 text-muted fs-13">
                                            <span>Showing {Math.min((page - 1) * limit + 1, pagination.total)} to {Math.min(page * limit, pagination.total)} of {pagination.total} entries</span>
                                            <span className="ms-2">| Items per page:</span>
                                            <select
                                                className="form-select form-select-sm"
                                                style={{ width: '70px' }}
                                                value={limit}
                                                onChange={(e) => {
                                                    setLimit(Number(e.target.value));
                                                    setPage(1);
                                                }}
                                            >
                                                <option value={20}>20</option>
                                                <option value={30}>30</option>
                                                <option value={50}>50</option>
                                                <option value={100}>100</option>
                                            </select>
                                        </div>

                                        <ul className="pagination pagination-sm mb-0">
                                            <li className={`page-item ${page === 1 ? 'disabled' : ''}`}>
                                                <button className="page-link" onClick={() => setPage(1)} disabled={page === 1}>
                                                    <i className="ri-double-left-line"></i>
                                                </button>
                                            </li>
                                            <li className={`page-item ${page === 1 ? 'disabled' : ''}`}>
                                                <button className="page-link" onClick={() => setPage(prev => Math.max(1, prev - 1))} disabled={page === 1}>
                                                    Prev
                                                </button>
                                            </li>
                                            
                                            {Array.from({ length: Math.min(5, pagination.totalPages) }, (_, i) => {
                                                let pNum;
                                                if (pagination.totalPages <= 5) {
                                                    pNum = i + 1;
                                                } else if (page <= 3) {
                                                    pNum = i + 1;
                                                } else if (page >= pagination.totalPages - 2) {
                                                    pNum = pagination.totalPages - 4 + i;
                                                } else {
                                                    pNum = page - 2 + i;
                                                }
                                                return (
                                                    <li key={pNum} className={`page-item ${page === pNum ? 'active' : ''}`}>
                                                        <button className="page-link" onClick={() => setPage(pNum)}>
                                                            {pNum}
                                                        </button>
                                                    </li>
                                                );
                                            })}

                                            <li className={`page-item ${page >= pagination.totalPages ? 'disabled' : ''}`}>
                                                <button className="page-link" onClick={() => setPage(prev => Math.min(pagination.totalPages, prev + 1))} disabled={page >= pagination.totalPages}>
                                                    Next
                                                </button>
                                            </li>
                                            <li className={`page-item ${page >= pagination.totalPages ? 'disabled' : ''}`}>
                                                <button className="page-link" onClick={() => setPage(pagination.totalPages)} disabled={page >= pagination.totalPages}>
                                                    <i className="ri-double-right-line"></i>
                                                </button>
                                            </li>
                                        </ul>
                                    </div>
                                )}
                            </>
                        )}
                    </div>
                </div>
            </div>

        </div>
    );
};

export default ListingApprovals;
